import {Box, Stack, Typography} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {Check, ChevronDown} from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import {GREEN_BUTTON} from '../../../theme';
import {
  ALL_DOCUMENT_ACCESS_ROLES,
  DocumentAccessRole,
  DocumentAccessRolesDescription,
  DocumentAccessRolesLabel,
} from './constants';

const SelectRole = ({
  value,
  onChange,
  label,
  loading = false,
  roles = ALL_DOCUMENT_ACCESS_ROLES,
  disabled = false,
  formControlSx = {width: '130px'},
  selectSx = {},
  footerComponent,
  headerComponent,
}) => (
  <FormControl fullWidth size="small" sx={formControlSx} variant="outlined">
    {label && <InputLabel id="select-role-label">{label}</InputLabel>}
    <Select
      defaultValue={DocumentAccessRole.Viewer}
      disabled={disabled}
      IconComponent={(p) => <ChevronDown size={16} {...p} />}
      label={label}
      labelId={label ? 'select-role-label' : ''}
      onChange={onChange}
      renderValue={(value) => DocumentAccessRolesLabel[value]}
      size="small"
      sx={{
        borderRadius: '0.5rem',
        fontWeight: 500,
        '& .MuiSelect-select': {
          borderWidth: '0',
        },
        '&.Mui-focused fieldset': {
          borderColor: `${GREEN_BUTTON}!important`,
        },
        '& legend': {
          width: label ? 'auto' : 0,
        },
        ...selectSx,
      }}
      value={value}
    >
      {headerComponent}
      {roles.map((role) => (
        <MenuItem
          key={role}
          sx={{
            fontWeight: 500,
            gap: '0.5rem',
            alignItems: 'flex-start',
            // To preserve spacing when there is or not an icon, we make them all have the icon,
            // but transparent when not selected. When selected, we make it visible.
            '&:not(.Mui-selected)': {
              svg: {
                color: 'transparent',
              },
            },
            // Per design, we don't want the selected MenuItem to have a background color
            '&.Mui-selected': {
              backgroundColor: 'transparent',
            },
          }}
          value={role}
        >
          <Check size={20} />
          <Stack direction="column" gap="0.125rem">
            {DocumentAccessRolesLabel[role]}
            <Typography
              color="text.secondary"
              sx={{fontSize: '0.75rem'}}
              variant="body2"
            >
              {DocumentAccessRolesDescription[role]}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
      {footerComponent && (
        <Box
          sx={{
            padding: '0.5rem 1rem',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {footerComponent}
        </Box>
      )}
    </Select>
  </FormControl>
);

SelectRole.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  loading: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  formControlSx: PropTypes.object,
  selectSx: PropTypes.object,
  headerComponent: PropTypes.node,
  footerComponent: PropTypes.node,
};

export default SelectRole;
